 import { Component, OnDestroy, OnInit } from '@angular/core';
  import { ActivatedRoute, Router } from '@angular/router';
  import { Subscription } from 'rxjs';
  import { Map as LeafletMap, tileLayer, LatLng, marker, Marker, latLngBounds, divIcon, Polyline, polyline } from 'leaflet';
  import { SosTrackingService, SosParticipant, SosLocationUpdate, SosRole } from '../../services/sos-tracking/sos-tracking.service';
  import { environment } from '../../../environments/environment';

  const SLOT_COLORS = ['#E53935', '#1E88E5', '#43A047', '#FB8C00', '#8E24AA', '#00897B'];

  // Set to false to revert to straight-line between agent and EC
  const USE_OSRM_ROUTING = true;

  // AC-4673 - which "turn on location" steps to show the EC
  type LocationHelpBrowser = 'samsung' | 'chrome-android' | 'android' | 'safari-ios' | 'chrome-ios' | 'other';

  interface ParticipantUi extends SosParticipant {
    isMe: boolean;
    distanceText?: string;
    responderNo?: number;
    label?: string;
    connectionStatus?: 'waiting' | 'connected' | 'disconnected';
  }

  @Component({
    selector: 'app-sos-track',
    templateUrl: './sos-track.component.html',
    styleUrls: ['./sos-track.component.scss']
  })
  export class SosTrackComponent implements OnInit, OnDestroy {

    sessionCode = '';
    participantToken = '';
    viewOnly = false; // true when opened via &view=1 (e.g. agent tapping EC's ack SMS)
    // AC-4674 - the sender sent a newer SOS: this viewer's link into it (shown with the ended message)
    newSessionUrl = '';

    infoMessage = '';
    errorMessage = '';
    statusText = 'Connecting...';
    isLive = true;
    isEnded = false;
    // AC-4629 - the session timed out because the phone went silent: not an all clear, so no "Safe now".
    isExpired = false;
    isEnding = false;
    hasReceivedFirstUpdate = false;
    panelOpen = false;
    locationDenied = false;
    showLocationInstructions = false;
    retryingLocation = false;
    locationHelpBrowser: LocationHelpBrowser = 'other';
    connectionLost = false;
    showReconnectDialog = false;
    showReconnectingBanner = false;
    connectionTerminated = false;
    private hasShownReconnectDialog = false;
    // AC-4621 - when the sender's phone stops sharing (app swiped closed, phone off, no signal),
    // the map just freezes; tell the EC after a few minutes instead of trusting a stale pin.
    senderSilentSince: Date | null = null;
    private lastSenderUpdateMs = 0;

    viewerRole: SosRole = 'responder';
    agentName = 'Agent';
    // AC-4636 - only agents get the "(Agent)" tag; a consumer sender (domiiRescue) shows just their name.
    senderIsAgent = true;

    private readonly CONNECTION_TIMEOUT_MS = (environment.sosConnectionTimeoutMinutes ?? 30) * 60 * 1000;

    map!: LeafletMap;
    options: any = {
      zoom: 15,
      center: new LatLng(37.7749, -122.4194),
      layers: [tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', { maxZoom: 19, attribution: '© OpenStreetMap' })]
    };

    private viewerParticipantId = '';
    private participants: SosParticipant[] = [];
    participantsUi: ParticipantUi[] = [];
    private markers = new globalThis.Map<string, Marker>();
    private polylines = new globalThis.Map<string, Polyline>();
    private routeCoords = new globalThis.Map<string, LatLng[]>();
    private lastRecalculation = new globalThis.Map<string, number>();
    private pendingOsrmFetches = new globalThis.Map<string, AbortController>();
    private hasBothParticipantsVisible = false;
    private readonly DEVIATION_THRESHOLD_MI = 0.062; // ~100 metres
    private readonly RECALC_COOLDOWN_MS = 30_000;    // 30 seconds
    private readonly EC_STATUS_STALE_MS = 180_000;   // 3 min without update → disconnected
    private readonly SENDER_SILENT_MS = 180_000;     // at least; see senderSilentMs (AC-4683)
    private readonly NOT_MOVING_MS = 120_000;        // AC-4683 - within 20 m (API) for 2 min = "Not moving"
    private statusTimer: any;

    // AC-4683 - the line under the sender: "Last update 3 min ago · Not moving · Battery 42%"
    senderStatusText = '';
    senderStatusStale = false;

    private locSub?: Subscription;
    private endedSub?: Subscription;
    private joinedSub?: Subscription;
    private joinedRefreshSub?: Subscription;
    private connectionStateSub?: Subscription;
    private lastAnyUpdateMs = 0;
    private geoWatchId?: number;
    private lastGpsPushMs = 0;
    private connectionCheckTimer?: any;
    private readonly RESPONDER_PUSH_INTERVAL = 15000;
    // AC-4633 - a locked phone or background tab misses live events; look the session up again
    // when the page comes back, so a missed end or move shows without waiting for the next update.
    private refreshInFlight = false;
    private lastVisibleRefreshMs = 0;
    private readonly VISIBLE_REFRESH_COOLDOWN_MS = 5000;
    private readonly onVisibilityChange = () => {
      if (document.visibilityState !== 'visible' || this.isEnded || this.connectionTerminated) return;
      if (Date.now() - this.lastVisibleRefreshMs < this.VISIBLE_REFRESH_COOLDOWN_MS) return;
      this.lastVisibleRefreshMs = Date.now();
      this.refreshSession();
    };

    constructor(private route: ActivatedRoute, private sosService: SosTrackingService, private router: Router) {}

    /** AC-4665: opened from a RescueMII SOS (/rescue/track), so the footer offers RescueMII. */
    get isRescueLink(): boolean {
      return this.route.snapshot.url[0]?.path === 'rescue';
    }

    get participantsCount(): number { return this.participantsUi.length; }

    get canMarkSafe(): boolean {
      const me = this.participantsUi.find(p => p.isMe);
      return !!me && me.role === 'sender' && !this.isEnded && !this.isEnding;
    }

    get canCancelSos(): boolean {
      const me = this.participantsUi.find(p => p.isMe);
      return !!me && me.role === 'sender' && !this.isEnded && !this.isEnding;
    }

    get canClose(): boolean { return this.isEnded && this.viewerRole === 'sender'; }

    ngOnInit(): void {
      // New URL: /sos/track?code=XXX&t=GUID[&view=1]
      // Old URL: /sos-track/:inviteCode?rt=GUID  (kept for old SMS links)
      this.sessionCode =
        (this.route.snapshot.queryParamMap.get('code') || '').trim() ||
        (this.route.snapshot.paramMap.get('inviteCode') || '').trim();

      this.participantToken =
        (this.route.snapshot.queryParamMap.get('t') || '').trim() ||
        this.readOldResponderToken();

      // view=1 means read-only — no GPS push even if the token belongs to a responder
      this.viewOnly = this.route.snapshot.queryParamMap.get('view') === '1';

      if (!this.sessionCode) {
        this.errorMessage = 'Invalid link. Missing session code.';
        this.statusText = 'Error';
        this.isLive = false;
        return;
      }

      this.statusText = 'Connecting...';
      this.errorMessage = '';

      this.sosService.initSession(this.sessionCode, this.participantToken).subscribe({
        next: (init) => {
          if (!init?.viewer?.participantId) {
            this.errorMessage = 'SOS session response is missing viewer context.';
            this.statusText = 'Error';
            this.isLive = false;
            return;
          }

          this.viewerParticipantId = init.viewer.participantId;
          this.viewerRole = init.viewer.role;
          this.participants = init.participants || [];
          this.isEnded = init.status === 'Ended';
          this.isExpired = this.isEnded && init.endReason === 'Expired';
          this.isLive = !this.isEnded;
          this.statusText = this.isEnded ? 'Tracking ended' : 'Live tracking';

          this.senderIsAgent = init.senderType !== 'Consumer';
          const sender = this.participants.find(p => p.role === 'sender');
          this.agentName = sender?.displayName?.trim() || (this.senderIsAgent ? 'Agent' : 'Sender');

          if (sender?.lastSeenUtc) {
            this.lastAnyUpdateMs = new Date(sender.lastSeenUtc).getTime();
            this.lastSenderUpdateMs = this.lastAnyUpdateMs;
          } else if (sender && isFinite(sender.lat) && isFinite(sender.lon) && !(sender.lat === 0 && sender.lon === 0)) {
            this.lastAnyUpdateMs = Date.now();
          }

          if (this.isEnded && init.endReason === 'Replaced') {
            this.showReplaced(init.message, init.newSessionUrl);
          } else if (this.isEnded) {
            this.infoMessage = this.viewerRole === 'sender' && !this.isExpired
              ? 'You already ended this SOS.'
              : (init.message?.trim() || `${this.agentName} has already cancelled this SOS and no longer needs help.`);
          } else {
            this.infoMessage = this.viewerRole === 'responder'
              ? 'You are viewing as a responder.'
              : this.senderIsAgent ? 'You are viewing as the agent.' : 'You are viewing as the sender.';
          }

          this.rebuildParticipantsUi();
          this.hasReceivedFirstUpdate = this.participantsUi.some(
            p => isFinite(p.lat) && isFinite(p.lon) && !(p.lat === 0 && p.lon === 0)
          );

          if (this.map) this.renderAllMarkers(true);

          // Only connect SignalR and join for live sessions
          if (!this.isEnded) {
            this.sosService.startSignalR(this.sessionCode, this.participantToken);

            // Fire HTTP join immediately — independent of SignalR WebSocket success.
            // connectionId is intentionally empty here: the backend broadcasts to all
            // connections sharing UserId=sessionCode, so the connectionId is unused.
            // This ensures the ack SMS reaches the agent even when the WebSocket
            // handshake fails (prod issue observed 5/27: Negotiate ✅, JoinSession ❌).
            if (this.participantToken && !this.viewOnly) {
              this.sosService.joinSession(this.sessionCode, this.participantToken, '').subscribe({
                next: () => {
                  console.log('[SOS] HTTP join succeeded');
                  this.sosService.notifySessionJoined(this.sessionCode);
                },
                error: (e) => console.error('[SOS] HTTP join failed:', e)
              });
            }

            // After the HTTP join completes, re-fetch to catch responders who joined
            // during the ~1-2 s gap between initSession and SignalR being ready
            this.joinedRefreshSub = this.sosService.onSessionJoined().subscribe((joinedCode) => {
              if (joinedCode.trim().toLowerCase() !== this.sessionCode.trim().toLowerCase()) return;
              this.refreshSession();
            });

            document.addEventListener('visibilitychange', this.onVisibilityChange);

            this.connectionStateSub = this.sosService.onConnectionStateChange().subscribe((state) => {
              if (state === 'reconnecting') {
                this.connectionLost = true;
                this.showReconnectingBanner = true;
              } else if (state === 'closed') {
                // Ignore intentional closes (Mark Safe / Cancel SOS / session ended)
                if (this.isEnded || this.connectionTerminated) return;
                this.connectionLost = true;
                this.showReconnectingBanner = false;
                if (!this.hasShownReconnectDialog) {
                  this.showReconnectDialog = true;
                  this.hasShownReconnectDialog = true;
                  this.playTone('alert');
                }
              } else if (state === 'reconnected') {
                this.connectionLost = false;
                this.showReconnectingBanner = false;
                this.hasShownReconnectDialog = false;
                // Catch up on what was missed while disconnected: the SOS may have ended,
                // pins may have moved, someone may have joined.
                this.refreshSession();
              }
              this.rebuildParticipantsUi();
            });
          }

          // Check connection status on load and every 60 seconds
          this.checkConnectionStatus();
          this.connectionCheckTimer = setInterval(() => {
            if (!this.isEnded) this.checkConnectionStatus();
          }, 30_000);
          this.statusTimer = setInterval(() => this.updateSenderStatus(), 15_000);

          this.locSub = this.sosService.onLocationUpdates().subscribe((u: SosLocationUpdate) => {
            if (this.isEnded || !u) return;
            if ((u.sessionCode || '').trim().toLowerCase() !== this.sessionCode.trim().toLowerCase()) return;

            this.hasReceivedFirstUpdate = true;
            this.lastAnyUpdateMs = Date.now();
            if (u.role === 'sender') this.lastSenderUpdateMs = this.lastAnyUpdateMs;
            const idx = this.participants.findIndex(p => p.participantId === u.participantId);
            if (idx >= 0) {
              this.participants[idx] = { ...this.participants[idx], lat: u.lat, lon: u.lon, lastSeenUtc: u.sentUtc,
                ...(u.role === 'sender' ? this.senderStatusFields(u) : {}) };
            } else if (u.role === 'responder' && u.participantId) {
              // Missed OnParticipantJoined (race condition) — add a partial record so the pin shows
              this.participants.push({
                participantId: u.participantId,
                role: 'responder',
                index: u.index,
                colorHex: u.colorHex,
                displayName: 'EC',
                lat: u.lat,
                lon: u.lon,
                lastSeenUtc: u.sentUtc
              });
            }
            this.rebuildParticipantsUi();
            this.renderAllMarkers(false);
            this.checkConnectionStatus();

            // Route deviation check — only for responders when OSRM is on
            if (USE_OSRM_ROUTING && u.role === 'responder') {
              this.checkDeviation(u.participantId, new LatLng(u.lat, u.lon));
            }
          });

          this.joinedSub = this.sosService.onParticipantJoined().subscribe((newP) => {
            if (!newP?.participantId || this.participants.some(p => p.participantId === newP.participantId)) return;
            this.participants.push(newP);
            this.rebuildParticipantsUi();
            if (this.map) this.renderAllMarkers(false);
            // Play chime so the agent knows an EC has joined
            if (this.viewerRole === 'sender') {
              this.playTone('join');
            }
          });

          this.endedSub = this.sosService.onTrackingEnded().subscribe((evt) => {
            if ((evt?.sessionCode || '').trim().toLowerCase() !== this.sessionCode.trim().toLowerCase()) return;
            // AC-4674 - replaced by a newer SOS: the broadcast can't carry each viewer's own link, so look it up
            if (evt?.endReason === 'Replaced') { this.endTrackingFromLookup(); return; }
            this.isExpired = evt?.endReason === 'Expired';
            this.endTracking(evt?.message);
          });

          if (this.participantToken && !this.viewOnly) {
            this.startResponderTracking();
          }
        },
        error: () => {
          this.errorMessage = 'Unable to load SOS session. Link may be invalid or expired.';
          this.statusText = 'Error';
          this.isLive = false;
        }
      });
    }

    ngOnDestroy(): void {
      this.locSub?.unsubscribe();
      this.endedSub?.unsubscribe();
      this.joinedSub?.unsubscribe();
      this.joinedRefreshSub?.unsubscribe();
      this.connectionStateSub?.unsubscribe();
      document.removeEventListener('visibilitychange', this.onVisibilityChange);
      this.sosService.stopMockLiveUpdates();
      this.stopResponderTracking();
      this.sosService.stopSignalR();
      if (this.connectionCheckTimer) clearInterval(this.connectionCheckTimer);
      if (this.statusTimer) clearInterval(this.statusTimer);
    }

    onMapReady(map: LeafletMap): void {
      this.map = map;
      map.attributionControl.setPrefix('<a href="https://www.domii.io" target="_blank" style="color:#F75A72;font-weight:700;">domii®</a>');
      this.renderAllMarkers(true);
    }

    togglePanel(): void { this.panelOpen = !this.panelOpen; }

    onMarkSafe(): void {
      if (this.isEnded || this.isEnding) return;
      this.isEnding = true;
      this.errorMessage = '';
      this.statusText = 'Ending SOS...';

      this.sosService.markSafe(this.sessionCode, this.participantToken).subscribe({
        next: () => {
          this.isEnded = true;
          this.isLive = false;
          this.statusText = 'Tracking ended';
          this.infoMessage = 'SOS ended successfully. You are now marked as safe.';
          this.panelOpen = false;
          this.rebuildParticipantsUi();
          this.sosService.stopSignalR();
          this.stopResponderTracking();
          this.isEnding = false;
        },
        error: () => {
          this.statusText = 'Error';
          this.errorMessage = 'Unable to end SOS. Please try again.';
          this.isEnding = false;
        }
      });
    }

    onCancelSos(): void {
      if (this.isEnded || this.isEnding) return;
      this.isEnding = true;
      this.errorMessage = '';
      this.statusText = 'Cancelling SOS...';

      this.sosService.cancelSos(this.sessionCode, this.participantToken).subscribe({
        next: () => {
          this.isEnded = true;
          this.isLive = false;
          this.statusText = 'Tracking ended';
          this.infoMessage = 'SOS cancelled. Your emergency contacts have been notified.';
          this.panelOpen = false;
          this.rebuildParticipantsUi();
          this.sosService.stopSignalR();
          this.stopResponderTracking();
          this.isEnding = false;
        },
        error: () => {
          this.statusText = 'Error';
          this.errorMessage = 'Unable to cancel SOS. Please try again.';
          this.isEnding = false;
        }
      });
    }

    onClose(): void { this.router.navigate(['/']); }
    /**
     * AC-4673 - the banner asks for location again before showing any steps. When the browser itself
     * lacks the phone's permission (Chrome's "Allow location? Continue" bar), that leads straight to the
     * phone's Allow prompt. Only when that fails too (the site is blocked, location is off) do the steps open.
     */
    onRetryLocation(): void {
      if (this.retryingLocation || !navigator.geolocation) { this.onShowInstructions(); return; }
      this.retryingLocation = true;
      navigator.geolocation.getCurrentPosition(
        () => {
          this.retryingLocation = false;
          this.locationDenied = false;
          this.stopResponderTracking();
          this.startResponderTracking();
        },
        () => {
          // Refused, or the phone's location is off or timed out: the steps cover all of these.
          this.retryingLocation = false;
          this.onShowInstructions();
        },
        { enableHighAccuracy: true, maximumAge: 5000, timeout: 30000 }
      );
    }

    onShowInstructions(): void {
      this.locationHelpBrowser = this.detectLocationHelpBrowser();
      this.showLocationInstructions = true;
    }
    onReloadForLocation(): void { window.location.reload(); }

    // AC-4673 - the generic "Site Settings icon" steps didn't match Samsung Internet or Chrome on Android,
    // so ECs couldn't find where to allow location. Pick the steps for the browser they're actually in.
    private detectLocationHelpBrowser(): LocationHelpBrowser {
      const ua = navigator.userAgent || '';
      const isIos = /iPhone|iPad|iPod/.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1);
      if (isIos) return /CriOS/.test(ua) ? 'chrome-ios' : 'safari-ios';
      if (/Android/.test(ua)) {
        if (/SamsungBrowser/.test(ua)) return 'samsung';
        if (/Chrome\//.test(ua) && !/EdgA|OPR|Firefox/.test(ua)) return 'chrome-android';
        return 'android';
      }
      return 'other';
    }
    onHideInstructions(): void { this.showLocationInstructions = false; }

    onRecenter(): void {
      if (!this.map) return;
      this.hasBothParticipantsVisible = false; // allow fitBounds to fire again
      this.renderAllMarkers(true);
    }

    private readOldResponderToken(): string {
      const q = (this.route.snapshot.queryParamMap.get('rt') || '').trim();
      if (q) return q;
      const frag = (this.route.snapshot.fragment || '').trim();
      if (!frag) return '';
      return (new URLSearchParams(frag).get('rt') || '').trim();
    }

    private rebuildParticipantsUi(): void {
      this.updateSenderStatus();
      const sender = this.participants.find(p => p.role === 'sender');
      const senderLat = sender?.lat ?? null;
      const senderLon = sender?.lon ?? null;

      const responders = this.participants
        .filter(p => p.role === 'responder')
        .slice()
        .sort((a, b) => (a.index ?? 9999) - (b.index ?? 9999));

      const responderNoById = new globalThis.Map<string, number>();
      responders.forEach((r, i) => responderNoById.set(r.participantId, r.index ?? (i + 1)));

      // Privacy: EC only sees the agent + themselves; view=1 (e.g. agent rejoining via ACK SMS) bypasses this
      const visible = (this.viewerRole === 'responder' && !this.viewOnly)
        ? this.participants.filter(p => p.role === 'sender' || p.participantId === this.viewerParticipantId)
        : this.participants;

      this.participantsUi = visible.map(p => {
        const isMe = p.participantId === this.viewerParticipantId;
        const responderNo = p.role === 'responder'
          ? (responderNoById.get(p.participantId) ?? p.index)
          : undefined;

        let distanceText: string | undefined;
        if (p.role === 'responder'
            && senderLat != null && senderLon != null
            && !(p.lat === 0 && p.lon === 0)
            && !!p.lastSeenUtc) {
          distanceText = `${this.sosService.distanceMiles(senderLat, senderLon, p.lat, p.lon).toFixed(1)} mi away`;
        }

        // Connection status for EC participants
        let connectionStatus: 'waiting' | 'connected' | 'disconnected' | undefined;
        if (p.role === 'responder') {
          if (this.isEnded) {
            connectionStatus = 'disconnected';
          } else if (isMe && this.connectionLost) {
            // My own lastSeenUtc can still be recent right after the live connection drops -
            // fall back to the real connection state instead.
            connectionStatus = 'disconnected';
          } else if (!p.lastSeenUtc || (p.lat === 0 && p.lon === 0)) {
            connectionStatus = 'waiting';
          } else {
            const msSince = Date.now() - new Date(p.lastSeenUtc).getTime();
            connectionStatus = msSince < this.EC_STATUS_STALE_MS ? 'connected' : 'disconnected';
          }
        }

        const baseName = (p.displayName || '').trim()
          .replace(/ - (Sender|Responder\s+\d+)$/i, '').trim()
          || (p.role !== 'sender' ? 'EC' : this.senderIsAgent ? 'Agent' : 'Sender');

        const roleLabel = p.role !== 'sender' ? '(EC)' : this.senderIsAgent ? '(Agent)' : '';
        const label = roleLabel ? `${baseName} ${roleLabel}` : baseName;

        return { ...p, isMe, distanceText, responderNo, label, connectionStatus };
      });
    }

    /**
     * AC-4633 - look the session up again and apply what the live connection missed: end the
     * page if the SOS ended meanwhile (with the real reason), otherwise move pins to the latest
     * positions and add anyone who joined.
     */
    private refreshSession(): void {
      if (this.isEnded || this.refreshInFlight) return;
      this.refreshInFlight = true;
      this.sosService.initSession(this.sessionCode, this.participantToken).subscribe({
        next: (fresh) => {
          this.refreshInFlight = false;
          if (this.isEnded) return;
          if (fresh.status === 'Ended') {
            this.isExpired = fresh.endReason === 'Expired';
            this.endTracking(fresh.message);
            if (fresh.endReason === 'Replaced') this.showReplaced(fresh.message, fresh.newSessionUrl);
            return;
          }
          this.mergeParticipants(fresh.participants);
          this.checkConnectionStatus();
        },
        error: () => { this.refreshInFlight = false; }
      });
    }

    private mergeParticipants(incoming: SosParticipant[]): void {
      let changed = false;
      for (const p of incoming) {
        const idx = this.participants.findIndex(e => e.participantId === p.participantId);
        if (idx < 0) {
          this.participants.push(p);
          changed = true;
          continue;
        }
        // Only a newer position than the one on screen - a live update may have beaten the lookup.
        const seenMs = p.lastSeenUtc ? new Date(p.lastSeenUtc).getTime() : 0;
        const shownMs = this.participants[idx].lastSeenUtc ? new Date(this.participants[idx].lastSeenUtc!).getTime() : 0;
        if (seenMs > shownMs && isFinite(p.lat) && isFinite(p.lon) && !(p.lat === 0 && p.lon === 0)) {
          this.participants[idx] = { ...this.participants[idx], lat: p.lat, lon: p.lon, lastSeenUtc: p.lastSeenUtc,
            ...(p.role === 'sender' ? this.senderStatusFields(p) : {}) };
          changed = true;
          if (p.role === 'sender') {
            this.lastSenderUpdateMs = Math.max(this.lastSenderUpdateMs, seenMs);
            this.lastAnyUpdateMs = Math.max(this.lastAnyUpdateMs, seenMs);
            this.hasReceivedFirstUpdate = true;
          }
        }
      }
      if (changed) {
        this.rebuildParticipantsUi();
        if (this.map) this.renderAllMarkers(false);
      }
    }

    private renderAllMarkers(fitBounds: boolean): void {
      if (!this.map) return;
      const valid = this.participantsUi.filter(p =>
        isFinite(p.lat) && isFinite(p.lon) &&
        !(p.lat === 0 && p.lon === 0) &&
        (p.role === 'sender' || !!p.lastSeenUtc)
      );
      for (const p of valid) this.upsertMarker(p);
      this.updatePolylines(valid);

      // Also auto-fit the first time both participants have valid locations
      const shouldFit = fitBounds || (!this.hasBothParticipantsVisible && valid.length >= 2);
      if (shouldFit && valid.length > 0) {
        if (valid.length >= 2) this.hasBothParticipantsVisible = true;
        this.map.fitBounds(latLngBounds(valid.map(p => new LatLng(p.lat, p.lon))).pad(0.25));
      }
    }

    private updatePolylines(valid: ParticipantUi[]): void {
      if (!this.map) return;
      const agent = valid.find(p => p.role === 'sender');
      if (!agent) return;

      const responders = valid.filter(p => p.role === 'responder');
      const agentLatLng = new LatLng(agent.lat, agent.lon);

      for (const r of responders) {
        const respLatLng = new LatLng(r.lat, r.lon);
        const color = r.colorHex || SLOT_COLORS[Math.min(r.responderNo ?? 1, SLOT_COLORS.length - 1)] || '#999';

        if (USE_OSRM_ROUTING) {
          this.drawOsrmRoute(r.participantId, agentLatLng, respLatLng, color);
        } else {
          this.drawStraightLine(r.participantId, agentLatLng, respLatLng, color);
        }
      }

      // Remove lines for responders no longer visible
      for (const [id, line] of this.polylines.entries()) {
        if (!responders.find(r => r.participantId === id)) {
          line.remove();
          this.polylines.delete(id);
          this.routeCoords.delete(id);
          this.lastRecalculation.delete(id);
        }
      }
    }

    private drawStraightLine(id: string, from: LatLng, to: LatLng, color: string): void {
      const existing = this.polylines.get(id);
      if (!existing) {
        const line = polyline([from, to], { color, weight: 3, opacity: 0.8 }).addTo(this.map);
        this.polylines.set(id, line);
      } else {
        existing.setLatLngs([from, to]);
      }
    }

    private drawOsrmRoute(id: string, from: LatLng, to: LatLng, color: string): void {
      // Cancel any in-flight request for this id to avoid stale results overwriting newer ones
      this.pendingOsrmFetches.get(id)?.abort();
      const controller = new AbortController();
      this.pendingOsrmFetches.set(id, controller);

      const url = `https://router.project-osrm.org/route/v1/driving/` +
        `${from.lng},${from.lat};${to.lng},${to.lat}?geometries=geojson&overview=full`;

      this.lastRecalculation.set(id, Date.now());

      fetch(url, { signal: controller.signal })
        .then(r => r.json())
        .then(data => {
          this.pendingOsrmFetches.delete(id);
          if (!this.map) return;
          const coords: LatLng[] = (data?.routes?.[0]?.geometry?.coordinates ?? [])
            .map(([lng, lat]: [number, number]) => new LatLng(lat, lng));

          if (!coords.length) {
            this.drawStraightLine(id, from, to, color);
            return;
          }

          this.routeCoords.set(id, coords);

          const existing = this.polylines.get(id);
          if (!existing) {
            const line = polyline(coords, { color, weight: 4, opacity: 0.85 }).addTo(this.map);
            this.polylines.set(id, line);
          } else {
            existing.setLatLngs(coords);
          }
        })
        .catch(err => {
          if ((err as Error)?.name === 'AbortError') return;
          this.drawStraightLine(id, from, to, color);
        });
    }

    private checkDeviation(responderId: string, newPos: LatLng): void {
      const coords = this.routeCoords.get(responderId);
      if (!coords?.length) return;

      const lastRecalc = this.lastRecalculation.get(responderId) ?? 0;
      if (Date.now() - lastRecalc < this.RECALC_COOLDOWN_MS) return;

      const offRoute = !coords.some(p =>
        this.sosService.distanceMiles(newPos.lat, newPos.lng, p.lat, p.lng) < this.DEVIATION_THRESHOLD_MI
      );

      if (offRoute) {
        const agent = this.participantsUi.find(p => p.role === 'sender');
        const r = this.participantsUi.find(p => p.participantId === responderId);
        if (!agent || !r) return;
        console.log(`[SOS] ${r.label} deviated — recalculating route`);
        this.drawOsrmRoute(responderId, newPos, new LatLng(agent.lat, agent.lon), r.colorHex || '#999');
      }
    }

    private upsertMarker(p: ParticipantUi): void {
      if (!this.map) return;
      const latLng = new LatLng(p.lat, p.lon);
      const label = (p.label || p.displayName || '').trim() || (p.role === 'sender' ? 'Sender' : 'Responder');
      const existing = this.markers.get(p.participantId);

      if (!existing) {
        const m = marker(latLng, { icon: this.buildDivIcon(p) });
        m.bindTooltip(label, { permanent: true, direction: 'top', offset: [0, -26] });
        m.addTo(this.map);
        this.markers.set(p.participantId, m);

        const senderTappable = p.role === 'sender' && this.viewerRole === 'responder' && !this.viewOnly;
        const responderTappable = p.role === 'responder' && (this.viewerRole === 'sender' || this.viewOnly);
        if (!p.isMe && (senderTappable || responderTappable)) {
          m.on('click', () => {
            const current = this.participants.find(x => x.participantId === p.participantId);
            const lat = current?.lat ?? p.lat;
            const lon = current?.lon ?? p.lon;
            const pUi = this.participantsUi.find(x => x.participantId === p.participantId);
            this.openDrivingDirections(lat, lon, pUi?.label || current?.displayName);
          });
        }
      } else {
        existing.setLatLng(latLng);
        existing.setTooltipContent(label);
      }
    }

    private buildDivIcon(p: ParticipantUi) {
      const slotIndex = p.role === 'sender' ? 0 : (p.responderNo ?? 1);
      const color = p.colorHex || SLOT_COLORS[Math.min(slotIndex, SLOT_COLORS.length - 1)] || '#999';
      const senderTappable = p.role === 'sender' && this.viewerRole === 'responder' && !this.viewOnly;
      const responderTappable = p.role === 'responder' && (this.viewerRole === 'sender' || this.viewOnly);
      const tappable = !p.isMe && (senderTappable || responderTappable);
      return divIcon({
        className: '',
        html: `<div style="width:44px;height:44px;border-radius:50%;background:${color};border:3px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,0.35);cursor:${tappable ? 'pointer' : 'default'};"></div>`,
        iconSize: [44, 44],
        iconAnchor: [22, 44]
      });
    }

    private startResponderTracking(): void {
      if (!this.participantToken || this.isEnded || !navigator.geolocation) return;
      if (this.viewOnly) return;           // view-only link — never push GPS
      if (this.viewerRole !== 'responder') return; // senders never push GPS from the web view
      this.lastGpsPushMs = 0; // push immediately on first fix
      this.geoWatchId = navigator.geolocation.watchPosition(
        (pos) => {
          // Skip low-accuracy fixes to prevent pin jumps (>100 m horizontal error)
          if (pos.coords.accuracy > 100) return;
          this.onGpsPosition(pos.coords.latitude, pos.coords.longitude);
        },
        (err) => {
          console.warn('[SOS] GPS watch error', err);
          if (err.code === 1) this.locationDenied = true;
        },
        { enableHighAccuracy: true, maximumAge: 5000 }
      );
    }

    private endTracking(message?: string): void {
      this.isEnded = true;
      this.isLive = false;
      this.isEnding = false;
      this.statusText = 'Tracking ended';
      this.infoMessage = message?.trim() || 'Tracking has ended.';
      this.panelOpen = false;
      // A page that ends while reconnecting (phone locked, then a newer SOS replaced this one) must not
      // keep "Reconnecting... please wait." or the reconnect prompt over the ended message.
      this.showReconnectingBanner = false;
      this.showReconnectDialog = false;
      this.connectionLost = false;
      this.rebuildParticipantsUi();
      this.sosService.stopSignalR();
      this.stopResponderTracking();
    }

    /**
     * AC-4674 - the sender sent a newer SOS, which ended this one. Say so and offer this viewer's own
     * link into the new one (kept view-only if this page was).
     */
    private showReplaced(message?: string, url?: string): void {
      this.newSessionUrl = url ? url + (this.viewOnly ? '&view=1' : '') : '';
      this.infoMessage = this.viewerRole === 'sender'
        ? 'You sent a new SOS.'
        : (message?.trim() || `${this.agentName} sent a new SOS.`);
    }

    /** AC-4633 - end with the session's own ending message when it can be looked up. */
    private endTrackingFromLookup(): void {
      this.sosService.initSession(this.sessionCode, this.participantToken).subscribe({
        next: (fresh) => {
          if (this.isEnded) return;
          this.isExpired = fresh.status === 'Ended' && fresh.endReason === 'Expired';
          this.endTracking(fresh.status === 'Ended' ? fresh.message : undefined);
          if (fresh.status === 'Ended' && fresh.endReason === 'Replaced') this.showReplaced(fresh.message, fresh.newSessionUrl);
        },
        error: () => { if (!this.isEnded) this.endTracking(); }
      });
    }

    private stopResponderTracking(): void {
      if (this.geoWatchId !== undefined) {
        navigator.geolocation.clearWatch(this.geoWatchId);
        this.geoWatchId = undefined;
      }
    }

    private onGpsPosition(lat: number, lon: number): void {
      if (!this.participantToken || !this.sessionCode || this.isEnded) return;
      const now = Date.now();
      if (now - this.lastGpsPushMs < this.RESPONDER_PUSH_INTERVAL) return;
      this.lastGpsPushMs = now;

      const idx = this.participants.findIndex(p => p.participantId === this.viewerParticipantId);
      if (idx >= 0) {
        this.participants[idx] = { ...this.participants[idx], lat, lon };
        this.rebuildParticipantsUi();
        this.renderAllMarkers(false);
        this.hasReceivedFirstUpdate = true;
      }
      this.sosService.pushParticipantLocation(this.sessionCode, this.participantToken, lat, lon).subscribe({
        // My "Connected" status comes from lastSeenUtc, so only a push the server accepted
        // counts - a rejected one (404 after the session ended, offline) must not show Connected.
        next: () => {
          const i = this.participants.findIndex(p => p.participantId === this.viewerParticipantId);
          if (i < 0 || this.isEnded) return;
          this.participants[i] = { ...this.participants[i], lastSeenUtc: new Date().toISOString() };
          this.rebuildParticipantsUi();
          this.renderAllMarkers(false);
        },
        error: (err) => {
          console.error('[SOS] pushParticipantLocation failed', err);
          // AC-4621 - the API answers 404 once the session has ended; stop sharing even if
          // the OnSessionEnded broadcast was missed (e.g. SignalR was disconnected).
          // AC-4633 - the 404 doesn't say why, so look the session up for the real message.
          if (err?.status === 404 && !this.isEnded) this.endTrackingFromLookup();
        }
      });
    }

    onReconnectYes(): void {
      this.showReconnectDialog = false;
      this.showReconnectingBanner = false;
      this.connectionLost = false;
      this.rebuildParticipantsUi();
      this.sosService.startSignalR(this.sessionCode, this.participantToken, true);
    }

    onReconnectNo(): void {
      this.showReconnectDialog = false;
      this.showReconnectingBanner = false;
      this.connectionTerminated = true;
      this.sosService.stopSignalR();
      this.stopResponderTracking();
    }

    // AC-4683 - a still phone may send only every 5 minutes (AC-4682), so "stopped" waits two of
    // its intervals plus a minute, like the app's own session window; never less than 3 minutes.
    private senderSilentMs(): number {
      const interval = this.participants.find(p => p.role === 'sender')?.intervalSeconds;
      return Math.max(this.SENDER_SILENT_MS, interval ? (2 * interval + 60) * 1000 : 0);
    }

    private senderStatusFields(x: { battery?: number; charging?: boolean; intervalSeconds?: number; lastMovedUtc?: string }) {
      return { battery: x.battery, charging: x.charging, intervalSeconds: x.intervalSeconds, lastMovedUtc: x.lastMovedUtc };
    }

    private updateSenderStatus(): void {
      // The status is for the emergency contacts; the sender already knows their own phone's state.
      const sender = this.participants.find(p => p.role === 'sender');
      if (this.isEnded || this.viewerRole === 'sender' || !sender?.lastSeenUtc) {
        this.senderStatusText = '';
        this.senderStatusStale = false;
        return;
      }

      const seenMs = new Date(sender.lastSeenUtc).getTime();
      const ageMs = Math.max(0, Date.now() - seenMs);
      this.senderStatusStale = ageMs > this.senderSilentMs();

      const parts: string[] = [];
      if (this.senderStatusStale) {
        parts.push(`No update for ${this.formatDuration(ageMs)}`);
      } else {
        parts.push(ageMs < 60_000 ? 'Last update just now' : `Last update ${this.formatDuration(ageMs)} ago`);
        const movedMs = sender.lastMovedUtc ? new Date(sender.lastMovedUtc).getTime() : NaN;
        if (isFinite(movedMs) && seenMs - movedMs >= this.NOT_MOVING_MS) parts.push('Not moving');
      }
      if (sender.battery != null) parts.push(`Battery ${Math.round(sender.battery)}%${sender.charging ? ' (charging)' : ''}`);
      this.senderStatusText = parts.join(' · ');
    }

    private formatDuration(ms: number): string {
      const min = Math.max(1, Math.floor(ms / 60_000));
      if (min < 60) return `${min} min`;
      const h = Math.floor(min / 60), m = min % 60;
      return m ? `${h} h ${m} min` : `${h} h`;
    }

    private checkConnectionStatus(): void {
      if (this.isEnded || this.connectionTerminated) {
        this.connectionLost = false;
        this.senderSilentSince = null;
        return;
      }

      // AC-4633 - while hidden or catching up, the last update can look stale only because this
      // page missed it; don't raise "stopped sharing" until the lookup has answered.
      const catchingUp = this.refreshInFlight || document.visibilityState === 'hidden';
      const senderSilent = this.viewerRole !== 'sender' && this.lastSenderUpdateMs > 0
        && Date.now() - this.lastSenderUpdateMs > this.senderSilentMs();
      if (!senderSilent) this.senderSilentSince = null;
      else if (!catchingUp || this.senderSilentSince) this.senderSilentSince = new Date(this.lastSenderUpdateMs);

      const wasLost = this.connectionLost;
      const now = Date.now();
      const sender = this.participantsUi.find(p => p.role === 'sender');

      if (this.viewerRole === 'responder') {
        const lastSeen = sender?.lastSeenUtc
          ? new Date(sender.lastSeenUtc).getTime()
          : this.lastAnyUpdateMs;
        if (lastSeen > 0) {
          this.connectionLost = (now - lastSeen) > this.CONNECTION_TIMEOUT_MS;
        }
      } else {
        const staleEc = this.participantsUi.find(p =>
          p.role === 'responder' && p.lastSeenUtc &&
          (now - new Date(p.lastSeenUtc).getTime()) > this.CONNECTION_TIMEOUT_MS
        );
        this.connectionLost = !!staleEc;
      }

      if (wasLost !== this.connectionLost) this.rebuildParticipantsUi();

      if (!wasLost && this.connectionLost && !this.hasShownReconnectDialog) {
        this.showReconnectDialog = true;
        this.hasShownReconnectDialog = true;
        this.playTone('alert');
      }
    }

    private openDrivingDirections(lat: number, lon: number, name?: string): void {
      if (!isFinite(lat) || !isFinite(lon) || (lat === 0 && lon === 0)) return;
      const dest = encodeURIComponent(`${lat},${lon}`);
      const url = name?.trim()
        ? `https://www.google.com/maps/dir/?api=1&destination=${dest}&travelmode=driving&query=${encodeURIComponent(name)}`
        : `https://www.google.com/maps/dir/?api=1&destination=${dest}&travelmode=driving`;
      window.open(url, '_blank');
    }

    /**
     * Play a short Web Audio tone.
     * 'join'  — two ascending notes (EC joined the session).
     * 'alert' — descending urgent tone (connection lost / reconnect dialog).
     * Silently no-ops if Web Audio is unsupported or blocked.
     */
    private playTone(type: 'join' | 'alert'): void {
      try {
        const AudioCtx = window.AudioContext || (window as any).webkitAudioContext;
        if (!AudioCtx) return;
        const ctx = new AudioCtx() as AudioContext;

        if (type === 'join') {
          // Two ascending sine notes: C5 → G5
          [523, 784].forEach((freq, i) => {
            const osc = ctx.createOscillator();
            const gain = ctx.createGain();
            osc.connect(gain);
            gain.connect(ctx.destination);
            osc.type = 'sine';
            osc.frequency.value = freq;
            const t = ctx.currentTime + i * 0.18;
            gain.gain.setValueAtTime(0, t);
            gain.gain.linearRampToValueAtTime(0.25, t + 0.02);
            gain.gain.exponentialRampToValueAtTime(0.001, t + 0.35);
            osc.start(t);
            osc.stop(t + 0.35);
          });
        } else {
          // Descending square-wave sweep: 880 Hz → 220 Hz
          const osc = ctx.createOscillator();
          const gain = ctx.createGain();
          osc.connect(gain);
          gain.connect(ctx.destination);
          osc.type = 'square';
          osc.frequency.setValueAtTime(880, ctx.currentTime);
          osc.frequency.exponentialRampToValueAtTime(220, ctx.currentTime + 0.5);
          gain.gain.setValueAtTime(0.15, ctx.currentTime);
          gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.5);
          osc.start(ctx.currentTime);
          osc.stop(ctx.currentTime + 0.5);
        }

        setTimeout(() => ctx.close().catch(() => {}), 1500);
      } catch {
        // Web Audio not available — silently ignore
      }
    }
  }